import { FormattedDate, FormattedNumber, useIntl } from 'react-intl'

import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import { fluidColumn } from '@/components/table-columns'

import type { EligiblePayout } from './api'

/**
 * The eligible-payouts grid on step 2 of the settlement builder.
 *
 * Named for its ROW type: `components/payouts-table.tsx` has its own `usePayoutColumns` over
 * `PayoutRow`, and two same-named hooks over different shapes is a trap for whoever greps next.
 */
export const useEligiblePayoutColumns = (): ColumnDef<EligiblePayout>[] => {
  const { formatMessage } = useIntl()

  return createColumns<EligiblePayout>([
    {
      accessorKey: 'partner_display_name',
      header: formatMessage({ id: 'admin.settlements.partner' }),
      ...fluidColumn({ min: 140, flex: 1.2 }),
      cell: ({ getValue, row }) => (
        <div className="flex flex-col">
          {String(getValue())}
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {row.original.partner_email}
          </Typography>
        </div>
      ),
    },
    {
      accessorKey: 'payout_method',
      header: formatMessage({ id: 'admin.settlements.method' }),
      ...fluidColumn({ min: 80, flex: 0.6 }),
    },
    {
      accessorKey: 'requested_amount',
      header: formatMessage({ id: 'admin.settlements.amount' }),
      ...fluidColumn({ min: 100, flex: 0.8, align: 'right' }),
      cell: ({ row }) => (
        <FormattedNumber
          currency="USD"
          style="currency"
          value={row.original.requested_amount}
        />
      ),
    },
    {
      accessorKey: 'created_at',
      header: formatMessage({ id: 'admin.settlements.requested' }),
      ...fluidColumn({ min: 100, flex: 0.8 }),
      // `FormattedDate` rather than `toLocaleDateString`, which formats in the BROWSER's
      // locale and ignores the app's. This app is mixed (several cells still use the native
      // call); react-intl is the direction of travel, and the settlements list beside this
      // builder was converted in the same pass so one feature does not format one field two
      // ways.
      cell: ({ getValue }) => (
        <FormattedDate
          day="numeric"
          month="short"
          value={String(getValue())}
        />
      ),
    },
  ])
}
